import { useContext, useEffect, useState } from 'react';
import Dropdown from 'components/Dropdown/Dropdown';
import SelectChannelItem from 'components/Dropdown/SelectChannelItem';
import NoImg from 'assets/img/no-img.jpg';
import { ModalContext } from 'context/ModalContext';
import { MODAL_ANIMATIONS, MODAL_TYPES } from '../constants';
import api from 'services/api';
import { RootState } from 'store';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';
import clsx from 'clsx';
import { CloseIcon } from 'components/icons/CloseIcon';
import { RedoIcon } from 'components/icons/RedoIcon';
import { ArchiveIcon } from 'components/icons/ArchiveIcon';
import { useLocation } from 'react-router-dom';
import useVideo from 'hooks/useVideo';
import moment from 'moment';
import { updateActivity } from 'store/activitySlice';

interface ShareProps {
  videoId: string;
  videoName: string;
  initChannel?: string;
  videoHashId: string;
  onCancel: () => void;
}

export default function ShareVideo({ videoId, videoHashId, videoName, initChannel, onCancel }: ShareProps) {
  const [selectedChannel, handleSelectedChannel] = useState<null & any>(initChannel ? initChannel : null);
  const [sharedChannels, handleSharedChannels] = useState([] as any[]);
  const channelList = useSelector((state: RootState) => state.channels.channelList);
  const { showModal } = useContext(ModalContext);
  const [searchVal, setSearchVal] = useState(selectedChannel ? `#${selectedChannel.name}` : '');
  const userData = useSelector((state: RootState) => state.user.user);
  const [filteredChannels, handleFilteredChannels] = useState([] as any[]);
  const location = useLocation();
  const dispatch = useDispatch();

  const channelIdRegex = /\/channel\/([^/]+)/;
  const channelMatch = location.pathname.match(channelIdRegex);
  const channelId = channelMatch ? channelMatch[1] : 'home';

  const [selectedVideo, setSelectedVideo] = useState(null as any);
  const { video, loading, selectVideo } = useVideo(videoHashId, channelId, videoId);

  const createChannel = () => {
    showModal(MODAL_TYPES.create_channel, {
      animation: MODAL_ANIMATIONS.fade,
      selectedChannel,
      videoId,
      videoHashId
    });
  };

  const selectPlaylist = () => {
    showModal(MODAL_TYPES.select_playlist, {
      animation: MODAL_ANIMATIONS.fade,
      selectedChannel,
      videoId,
      videoHashId
    });
  };

  const share = async () => {
    const response = await api.post(`api/videos/channel/${selectedChannel.hash_id}/share/${videoId}/`);
    const data = await response.data;
    const payload = {
      key: channelId,
      id: data.id,
      channels: data.channels
    };
    dispatch(updateActivity(payload));
    onCancel();
  };

  const archive = async () => {
    await api.post(`api/videos/channel/${selectedChannel.hash_id}/sharing/${selectedChannel.share.id}/`);
    onCancel();
  };

  const reShare = async () => {
    await api.put(`api/videos/channel/${selectedChannel.hash_id}/sharing/${selectedChannel.share.id}/`);
    onCancel();
  };

  const deleteChannelVideo = async () => {
    await api.delete(`api/videos/channel/${selectedChannel.hash_id}/sharing/${selectedChannel.share.id}/`);
    onCancel();
  };

  useEffect(() => {
    if (videoId) {
      api.get(`api/videos/${videoId}/shares/`).then((res) => {
        if (selectedChannel) {
          const sharedChannel = res.data.find((item: any) => item.channel_hash_id === selectedChannel.hash_id);
          const selectedSharedChannel = { ...selectedChannel };
          selectedSharedChannel['share'] = sharedChannel;
          selectedSharedChannel['playlists'] = sharedChannel?.playlists || [];
          handleSelectedChannel(selectedSharedChannel);
        }
        handleSharedChannels(res.data);
      });
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchVal(e.target.value);
  };

  useEffect(() => {
    if (selectVideo) {
      setSelectedVideo(selectVideo);
    } else {
      setSelectedVideo(video);
    }
  }, [loading]);

  useEffect(() => {
    const channels = channelList
      .filter((item) => item.name.toLowerCase().includes(searchVal?.toLowerCase()))
      .sort((a, b) => {
        if (
          a.name.toLowerCase().indexOf(searchVal?.toLowerCase()) <
          b.name.toLowerCase().indexOf(searchVal?.toLowerCase())
        ) {
          return -1;
        }
        if (
          a.name.toLowerCase().indexOf(searchVal?.toLowerCase()) >
          b.name.toLowerCase().indexOf(searchVal?.toLowerCase())
        ) {
          return 1;
        }
        return 0;
      });
    handleFilteredChannels(channels);
  }, [searchVal]);

  const selectChannel = (index: number) => {
    const sharedChannel = sharedChannels.find((item: any) => item.channel_hash_id === filteredChannels[index].hash_id);
    const selectedSharedChannel = { ...filteredChannels[index] };
    selectedSharedChannel['share'] = sharedChannel;
    selectedSharedChannel['playlists'] = sharedChannel?.playlists || [];
    handleSelectedChannel(selectedSharedChannel);
    setSearchVal(`#${filteredChannels[index]?.name}`);
  };

  return (
    <div className="transparent font-lato">
      <div className="relative border bg-gray-bg-dark border-gray-300 rounded-lg bg-white pb-7 pt-9 px-7 w-screen md:w-[622px] md:min-h-[300px]">
        <div className="flex">
          <span className="font-bold text-2xl leading-6 text-[#1A1C1D]">Share in a channel</span>
          <CloseIcon className="ml-auto w-5 h-5 cursor-pointer opacity-70 hover:opacity-100" onClick={onCancel} />
        </div>
        <div className="mt-8">
          <Dropdown
            DropdownItem={SelectChannelItem}
            menus={filteredChannels}
            handleClick={selectChannel}
            className="rounded-lg border"
          >
            <input
              type="text"
              value={searchVal}
              className="rounded-lg border-0 truncate w-full h-10 text-base focus:ring-0"
              onChange={handleChange}
              onFocus={() => {
                setSearchVal('');
                handleSelectedChannel(null);
              }}
            />
          </Dropdown>
        </div>
        {selectedChannel?.share ? (
          <div className="mt-4 bg-[#fcebb1] p-3 rounded-md">
            {selectedChannel.share.is_archived ? (
              <div className="flex mt-7">
                <ArchiveIcon />
                <p className="text-yellow-100 ml-2 font-bold">
                  The video was archived on {selectedChannel.share.f_date_archived} by
                </p>
              </div>
            ) : (
              <p className="font-bold text-sm text-[#1A1C1D]">
                The video was shared in this channel on{'  '}
                {moment(new Date(selectedChannel.share?.date_shared)).format('MMM DD, YYYY')}
              </p>
            )}
            <div className="flex mt-4 mb-2 font-bold text-sm text-[#1A1C1D] items-center">
              {selectedChannel.share.contributor.avatar ? (
                <>
                  <img alt="image" className="w-8 h-8 rounded-full" src={selectedChannel.share.contributor.avatar} />
                </>
              ) : (
                <>
                  <img alt="image" className="w-8 h-8 rounded-full" src={NoImg} />
                </>
              )}
              <span className="ml-2">Shared by {selectedChannel.share.contributor.name}</span>
            </div>
          </div>
        ) : (
          <textarea
            className="rounded-lg w-full border border-[#DCDCDC] py-1 px-3 focus:border-[#DCDCDC] mt-4 outline-none resize-none"
            rows={5}
            id="comment"
            placeholder="Add a comment (optional)"
            spellCheck="false"
          />
        )}
        <div className="mt-6 font-lato text-sm leading-[21px] font-bold">
          <p>Title: {videoName || selectedVideo?.name}</p>
          <div className="border rounded-md p-2 mt-4 h-20 overflow-y-auto">
            {sharedChannels.filter((channel: any) => !channel.is_archived).length ? (
              <p className="text-[#8083A3] line-clamp-3">
                Shared in
                {sharedChannels
                  .filter((channel: any) => !channel.is_archived)
                  .map(
                    (channel: any) => channelList.find((item: any) => item.hash_id === channel.channel_hash_id)?.name
                  )
                  .filter((item) => item)
                  .map((item: string, idx: number) => (
                    <span className="text-[#1664A3]" key={uuid()}>
                      {idx !== 0 && ', '}
                      {' #'}
                      {item}
                    </span>
                  ))}
              </p>
            ) : (
              <p className="text-[#8083A3]">Not shared in any channels</p>
            )}
          </div>
        </div>
        <div className="flex items-center mt-5">
          <img
            className="rounded-full cursor-pointer w-8 h-8 mr-2"
            src={selectedVideo?.owner?.avatar ? selectedVideo?.owner?.avatar : NoImg}
            alt="avatar"
          />
          <div className="text-sm text-[#1A1C1D]">
            <p className="font-bold">
              Owned by{' '}
              {selectedVideo && selectedVideo?.owner?.user_id === userData.user_id ? 'you' : selectedVideo?.owner?.name}
            </p>
            {/* <p className="font-normal text-[#8083A3]">Project Manager</p> */}
          </div>
        </div>
        <div className="flex mt-5">
          <div
            className="w-28 sm:w-40 h-8 sm:h-11 rounded-[10px] text-xs sm:text-base font-bold flex items-center justify-center bg-green-600 cursor-pointer text-white "
            onClick={createChannel}
          >
            Create a channel
          </div>

          {selectedChannel?.share ? (
            selectedChannel.share.is_archived ? (
              <>
                <div
                  className="w-20 sm:w-24 h-8 sm:h-11 bg-red-400 rounded-[10px] cursor-pointer text-white text-xs sm:text-base font-bold flex items-center justify-center  ml-auto"
                  onClick={deleteChannelVideo}
                >
                  Remove
                </div>
                <div
                  className="px-2 h-9 sm:h-11 bg-green-600 rounded-[10px] cursor-pointer text-white text-xs sm:text-base font-bold flex items-center justify-center ml-2"
                  onClick={reShare}
                >
                  <RedoIcon className="w-4 h-4 fill-white" />
                  &nbsp; Share again
                </div>
              </>
            ) : (
              <>
                <div
                  className={clsx(
                    'w-24 sm:w-40 h-8 sm:h-11 rounded-[10px] text-xs sm:text-base font-bold flex items-center justify-center ml-auto',
                    {
                      'bg-buttonDisabledBg text-white pointer-events-none': !selectedChannel,
                      'bg-green-600 cursor-pointer text-white ': selectedChannel
                    }
                  )}
                  onClick={selectPlaylist}
                >
                  Select collections
                </div>

                <div
                  className="w-20 sm:w-28 h-8 sm:h-11 bg-blue-text rounded-[10px] cursor-pointer text-white text-xs sm:text-base font-bold flex items-center justify-center ml-2"
                  onClick={archive}
                >
                  <ArchiveIcon className="w-4 h-4 fill-white" />
                  &nbsp; Archive
                </div>
              </>
            )
          ) : (
            <>
              <div
                className={clsx(
                  'w-24 sm:w-40 h-8 sm:h-11 rounded-[10px] text-xs sm:text-base font-bold flex items-center justify-center ml-auto',
                  {
                    'bg-buttonDisabledBg text-white pointer-events-none': !selectedChannel,
                    'bg-green-600 cursor-pointer text-white ': selectedChannel
                  }
                )}
                onClick={selectPlaylist}
              >
                Select collections
              </div>
              <div
                className={clsx(
                  'w-20 sm:w-40 h-8 sm:h-11 rounded-[10px] text-xs sm:text-base font-bold flex items-center justify-center ml-2',
                  {
                    'bg-buttonDisabledBg text-white pointer-events-none': !selectedChannel,
                    'bg-green-600 cursor-pointer text-white ': selectedChannel
                  }
                )}
                onClick={share}
              >
                Share
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
